import React, { useState, useEffect } from "react";
import {
  Form,
  Button,
  Card,
  Row,
  Col,
  Image,
  InputGroup,
  ListGroup,
} from "react-bootstrap";
import {
  useGetSettingsQuery,
  useUpdateSettingsMutation,
} from "../../../redux/api/SettingsApi/settingsApi";
import LoaderSpinner from "../../../Components/LoaderSpinner";
import {
  FaSave,
  FaGlobe,
  FaShareAlt,
  FaAddressCard,
  FaImage,
  FaPlus,
  FaTrash,
} from "react-icons/fa";
import { message } from "antd";

const SiteSettings = () => {
  const { data: settings, isLoading } = useGetSettingsQuery();
  const [updateSettings, { isLoading: isUpdating }] =
    useUpdateSettingsMutation();

  const [formData, setFormData] = useState({
    footer_description: "",
    footer_dynamic_text: "",
    newsletter_description: "",
    facebook_url: "",
    twitter_url: "",
    instagram_url: "",
    linkedin_url: "",
    address: "",
    phone: "",
    email: "",
    working_hours: "",
    footer_bottom_menus: [],
    meta_title: "",
    meta_keyword: "",
    meta_description: "",
  });

  const [newMenu, setNewMenu] = useState({ label: "", url: "" });
  const [logos, setLogos] = useState({ logo: null, footer_logo: null });
  const [previews, setPreviews] = useState({ logo: "", footer_logo: "" });

  useEffect(() => {
    if (settings) {
      setFormData({
        footer_description: settings.footer_description || "",
        footer_dynamic_text: settings.footer_dynamic_text || "",
        newsletter_description: settings.newsletter_description || "",
        facebook_url: settings.facebook_url || "",
        twitter_url: settings.twitter_url || "",
        instagram_url: settings.instagram_url || "",
        linkedin_url: settings.linkedin_url || "",
        address: settings.address || "",
        phone: settings.phone || "",
        email: settings.email || "",
        working_hours: settings.working_hours || "",
        footer_bottom_menus: settings.footer_bottom_menus || [],
        meta_title: settings.meta_title || "",
        meta_keyword: settings.meta_keyword || "",
        meta_description: settings.meta_description || "",
      });
      setPreviews({
        logo: settings.logo || "",
        footer_logo: settings.footer_logo || "",
      });
    }
  }, [settings]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (files && files[0]) {
      setLogos((prev) => ({ ...prev, [name]: files[0] }));
      setPreviews((prev) => ({
        ...prev,
        [name]: URL.createObjectURL(files[0]),
      }));
    }
  };

  const handleAddMenu = () => {
    if (newMenu.label && newMenu.url) {
      setFormData((prev) => ({
        ...prev,
        footer_bottom_menus: [...prev.footer_bottom_menus, newMenu],
      }));
      setNewMenu({ label: "", url: "" });
    }
  };

  const handleRemoveMenu = (index) => {
    setFormData((prev) => ({
      ...prev,
      footer_bottom_menus: prev.footer_bottom_menus.filter(
        (_, i) => i !== index,
      ),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = new FormData();

    // Append all text fields
    Object.keys(formData).forEach((key) => {
      if (key === "footer_bottom_menus") {
        data.append(key, JSON.stringify(formData[key]));
      } else {
        data.append(key, formData[key]);
      }
    });

    // Append files
    if (logos.logo) data.append("logo", logos.logo);
    if (logos.footer_logo) data.append("footer_logo", logos.footer_logo);

    try {
      await updateSettings(data).unwrap();
      message.success("Settings updated successfully");
    } catch (err) {
      message.error(err.data?.error || "Failed to update settings");
    }
  };

  if (isLoading)
    return (
      <div className="text-center py-5">
        <LoaderSpinner />
      </div>
    );

  return (
    <div className="container-fluid py-4 fade-in">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="fw-bold text-dark mb-0">Site Settings</h2>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={isUpdating}
          className="d-flex align-items-center gap-2 px-4 shadow-sm rounded-pill fw-semibold"
        >
          {isUpdating ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              Saving...
            </>
          ) : (
            <>
              <FaSave className="me-2" /> Save Settings
            </>
          )}
        </Button>
      </div>

      <Form onSubmit={handleSubmit}>
        <Row className="g-4">
          {/* Logo Section */}
          <Col lg={4}>
            <Card className="border-0 shadow-sm rounded-4 h-100">
              <Card.Header className="bg-white border-0 pt-4 px-4">
                <h5 className="fw-bold mb-0">
                  <FaImage className="text-primary me-2" /> Logos
                </h5>
              </Card.Header>
              <Card.Body className="p-4">
                <Row className="g-4 text-center">
                  <Col xs={12}>
                    <div
                      className="mb-3 border rounded-3 p-3 bg-light d-flex align-items-center justify-content-center"
                      style={{ height: "120px" }}
                    >
                      {previews.logo ? (
                        <Image
                          src={previews.logo}
                          style={{ maxHeight: "100%", maxWidth: "100%" }}
                        />
                      ) : (
                        <span className="text-muted">No Main Logo</span>
                      )}
                    </div>
                    <Form.Group>
                      <Form.Label className="small fw-bold">
                        Main Header Logo
                      </Form.Label>
                      <Form.Control
                        type="file"
                        name="logo"
                        size="sm"
                        onChange={handleFileChange}
                        accept="image/*"
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={12}>
                    <div
                      className="mb-3 border rounded-3 p-3 bg-light d-flex align-items-center justify-content-center"
                      style={{ height: "120px" }}
                    >
                      {previews.footer_logo ? (
                        <Image
                          src={previews.footer_logo}
                          style={{ maxHeight: "100%", maxWidth: "100%" }}
                        />
                      ) : (
                        <span className="text-muted">No Footer Logo</span>
                      )}
                    </div>
                    <Form.Group>
                      <Form.Label className="small fw-bold">
                        Footer Logo
                      </Form.Label>
                      <Form.Control
                        type="file"
                        name="footer_logo"
                        size="sm"
                        onChange={handleFileChange}
                        accept="image/*"
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>

          {/* General Footer Content */}
          <Col lg={8}>
            <Card className="border-0 shadow-sm rounded-4 mb-4">
              <Card.Header className="bg-white border-0 pt-4 px-4">
                <h5 className="fw-bold mb-0">
                  <FaGlobe className="text-primary me-2" /> Footer & Newsletter
                </h5>
              </Card.Header>
              <Card.Body className="p-4">
                <Row className="g-3">
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label className="fw-bold small">
                        Footer Bio Description
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="footer_description"
                        value={formData.footer_description}
                        onChange={handleChange}
                        placeholder="Short description for footer top section..."
                      />
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label className="fw-bold small">
                        Footer Dynamic Goal Text (use '—' as separator)
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="footer_dynamic_text"
                        value={formData.footer_dynamic_text}
                        onChange={handleChange}
                        placeholder="e.g. Success isn’t just built on code — purpose, people, and progress"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label className="fw-bold small">
                        Newsletter Hook Description
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        name="newsletter_description"
                        value={formData.newsletter_description}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm rounded-4">
              <Card.Header className="bg-white border-0 pt-4 px-4">
                <h5 className="fw-bold mb-0">
                  <FaShareAlt className="text-primary me-2" /> Social Media
                  Links
                </h5>
              </Card.Header>
              <Card.Body className="p-4">
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-bold small">
                        Facebook URL
                      </Form.Label>
                      <Form.Control
                        type="url"
                        name="facebook_url"
                        value={formData.facebook_url}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-bold small">
                        X (Twitter) URL
                      </Form.Label>
                      <Form.Control
                        type="url"
                        name="twitter_url"
                        value={formData.twitter_url}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-bold small">
                        Instagram URL
                      </Form.Label>
                      <Form.Control
                        type="url"
                        name="instagram_url"
                        value={formData.instagram_url}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-bold small">
                        LinkedIn URL
                      </Form.Label>
                      <Form.Control
                        type="url"
                        name="linkedin_url"
                        value={formData.linkedin_url}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>

          {/* Contact Details */}
          <Col lg={6}>
            <Card className="border-0 shadow-sm rounded-4 h-100">
              <Card.Header className="bg-white border-0 pt-4 px-4">
                <h5 className="fw-bold mb-0">
                  <FaAddressCard className="text-primary me-2" /> Contact
                  Information
                </h5>
              </Card.Header>
              <Card.Body className="p-4">
                <Row className="g-3">
                  <Col xs={12}>
                    <Form.Group>
                      <Form.Label className="fw-bold small">
                        Office Address
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-bold small">
                        Phone Number
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-bold small">
                        Email Address
                      </Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={12}>
                    <Form.Group>
                      <Form.Label className="fw-bold small">
                        Working Hours (e.g. Mon-Sat: 10AM-6PM)
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="working_hours"
                        value={formData.working_hours}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm rounded-4 mt-4">
              <Card.Header className="bg-white border-0 pt-4 px-4">
                <h5 className="fw-bold mb-0">
                  <FaGlobe className="text-primary me-2" /> Global SEO Keywords
                </h5>
              </Card.Header>
              <Card.Body className="p-4">
                <Row className="g-3">
                  <Col xs={12}>
                    <Form.Group>
                      <Form.Label className="fw-bold small">
                        Meta Title
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="meta_title"
                        value={formData.meta_title}
                        onChange={handleChange}
                        placeholder="Default SEO meta title..."
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={12}>
                    <Form.Group>
                      <Form.Label className="fw-bold small">
                        Meta Keywords
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        name="meta_keyword"
                        value={formData.meta_keyword}
                        onChange={handleChange}
                        placeholder="keyword1, keyword2, keyword3..."
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={12}>
                    <Form.Group>
                      <Form.Label className="fw-bold small">
                        Meta Description
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="meta_description"
                        value={formData.meta_description}
                        onChange={handleChange}
                        placeholder="Default SEO description..."
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>

          {/* Bottom Menus */}
          <Col lg={6}>
            <Card className="border-0 shadow-sm rounded-4 h-100">
              <Card.Header className="bg-white border-0 pt-4 px-4">
                <h5 className="fw-bold mb-0">
                  <FaGlobe className="text-primary me-2" /> Footer Bottom
                  Navigation
                </h5>
              </Card.Header>
              <Card.Body className="p-4">
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold small">
                    Add Menu Item
                  </Form.Label>
                  <InputGroup>
                    <Form.Control
                      placeholder="Label (e.g. Privacy)"
                      value={newMenu.label}
                      onChange={(e) =>
                        setNewMenu({ ...newMenu, label: e.target.value })
                      }
                    />
                    <Form.Control
                      placeholder="URL (e.g. /privacy)"
                      value={newMenu.url}
                      onChange={(e) =>
                        setNewMenu({ ...newMenu, url: e.target.value })
                      }
                    />
                    <Button variant="outline-primary" onClick={handleAddMenu}>
                      <FaPlus />
                    </Button>
                  </InputGroup>
                </Form.Group>

                <ListGroup variant="flush" className="border rounded-3">
                  {formData.footer_bottom_menus.map((menu, index) => (
                    <ListGroup.Item
                      key={index}
                      className="d-flex justify-content-between align-items-center py-2"
                    >
                      <div>
                        <strong>{menu.label}</strong>{" "}
                        <span className="text-muted small ms-2">
                          {menu.url}
                        </span>
                      </div>
                      <Button
                        variant="link"
                        size="sm"
                        className="text-danger p-0"
                        onClick={() => handleRemoveMenu(index)}
                      >
                        <FaTrash />
                      </Button>
                    </ListGroup.Item>
                  ))}
                  {formData.footer_bottom_menus.length === 0 && (
                    <div className="p-3 text-center text-muted small">
                      No custom footer menus added.
                    </div>
                  )}
                </ListGroup>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default SiteSettings;
